import React, { useMemo, useState } from "react";
import {
  ArrowForwardRounded,
  LocalHospitalRounded,
  LockRounded,
  PersonRounded,
  ShieldRounded,
  VisibilityOffRounded,
  VisibilityRounded,
  AutoGraphRounded,
  BadgeRounded,
  SupportAgentRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { toast } from "react-toastify";
import { BASE_URL } from "../constants";
import logo from "../images/PULSE.png";
import backgroundImage from "../images/login.png";
import { authState } from "../recoil/authAtom";
import { normalizeAuthIdentity } from "../utils/auth";
import { setCsrfToken } from "../utils/api";

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    background: "#fbfcff",
    fontSize: "0.9rem",
    "& fieldset": { borderColor: "#dbe3f0" },
    "&:hover fieldset": { borderColor: "#8295bd" },
    "&.Mui-focused fieldset": { borderColor: "#3157c9", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: "#71809a", fontSize: "0.86rem" },
  "& .MuiFormHelperText-root": { ml: 0.5, mt: 0.6 },
};

const PasswordField = ({ value, onChange, error, show, setShow }) => (
  <TextField
    fullWidth
    size="small"
    label="Enter your password"
    name="password"
    value={value}
    onChange={onChange}
    type={show ? "text" : "password"}
    error={Boolean(error)}
    helperText={error}
    sx={fieldStyles}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <LockRounded sx={{ color: "#71809a", fontSize: 19 }} />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            edge="end"
            size="small"
            aria-label={show ? "Hide password" : "Show password"}
            onClick={() => setShow((current) => !current)}
            sx={{ color: "#71809a" }}
          >
            {show ? <VisibilityOffRounded /> : <VisibilityRounded />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

const NewLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const setAuth = useSetRecoilState(authState);
  const initialMode = useMemo(() => "user", [location.pathname]);
  const [mode, setMode] = useState(initialMode);
  const [userForm, setUserForm] = useState({
    stateCode: "",
    orgCode: "",
    userCode: "",
    password: "",
  });
  const [doctorForm, setDoctorForm] = useState({
    username1: "",
    username2: "",
    username3: "",
    password: "",
  });
  const [userErrors, setUserErrors] = useState({});
  const [doctorErrors, setDoctorErrors] = useState({});
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showDoctorPassword, setShowDoctorPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
  };

  const updateForm = (setter, errorSetter) => (event) => {
    const { name, value } = event.target;
    setter((current) => ({ ...current, [name]: value }));
    errorSetter((current) => ({ ...current, [name]: "" }));
  };

  const submitUser = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!userForm.stateCode) errors.stateCode = "Enter state code";
    if (!userForm.orgCode) errors.orgCode = "Enter organisation code";
    if (!userForm.userCode) errors.userCode = "Enter user code";
    if (!userForm.password) errors.password = "Enter your password";
    setUserErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}api/user/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: `${userForm.stateCode}-${userForm.orgCode}-${userForm.userCode}`,
          userPassword: userForm.password,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success)
        throw new Error(data.message || "Unable to sign in. Please try again.");

      setCsrfToken(data.csrfToken);
      sessionStorage.removeItem("litss_doctor_session");
      const user = data.user;
      const org = user.organizationId || {};
      localStorage.setItem("userId", user._id || "");
      localStorage.setItem("userName", user.userName || "User");
      localStorage.setItem("orgID", org.orgID || "Organization ID");
      localStorage.setItem("orgName", org.orgName || "Organization Name");
      localStorage.setItem("orgPhone", org.orgPhone || "Organization Phone");
      localStorage.setItem(
        "orgAddress1",
        org.orgAddress1 || "Organization address 1",
      );
      localStorage.setItem(
        "orgAddress2",
        org.orgAddress1 || "Organization address 2",
      );
      localStorage.setItem("orgEmail", org.orgEmail || "");
      localStorage.setItem("orgLogo", org.orgLogoUrl || "");
      setAuth(normalizeAuthIdentity(user, "user"));
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.message || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitDoctor = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!doctorForm.username1 || !doctorForm.username2 || !doctorForm.username3)
      errors.username = "Enter your complete username";
    if (!doctorForm.password) errors.password = "Enter your password";
    setDoctorErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}api/doctor/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: `${doctorForm.username1}-${doctorForm.username2}-${doctorForm.username3}`,
          password: doctorForm.password,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success)
        throw new Error(data.message || "Unable to sign in. Please try again.");

      setCsrfToken(data.csrfToken);
      const doctor = data.doctor || data.user;
      const organization = doctor.organizationId || doctor.organization || {};
      const doctorName =
        doctor.name || doctor.doctorName || doctor.userName || doctor.username;
      const organizationName =
        organization.orgName ||
        organization.name ||
        doctor.orgName ||
        doctor.organizationName;
      if (doctor._id) localStorage.setItem("userId", doctor._id);
      if (doctorName) localStorage.setItem("userName", doctorName);
      if (organizationName) localStorage.setItem("orgName", organizationName);
      if (organization.orgLogoUrl || organization.logoUrl || doctor.orgLogoUrl)
        localStorage.setItem(
          "orgLogo",
          organization.orgLogoUrl || organization.logoUrl || doctor.orgLogoUrl,
        );
      sessionStorage.setItem("litss_doctor_session", "true");
      setAuth(normalizeAuthIdentity(doctor, "doctor"));
      navigate("/dashboard/patient", { replace: true });
    } catch (error) {
      toast.error(error.message || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const FormTitle = ({ icon, title, description }) => (
    <>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.15,
          py: 0.6,
          mb: 2.1,
          borderRadius: "100px",
          background: "#eaf0ff",
          color: "#3157c9",
        }}
      >
        {icon}
        <Typography
          sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.3px" }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: "1.75rem", sm: "2.05rem" },
          fontWeight: 800,
          color: "#14213d",
          letterSpacing: "-1px",
          lineHeight: 1.2,
          mb: 0.75,
        }}
      >
        Welcome back. {mode === "doctor" && "Doctor!"}
      </Typography>
      <Typography
        sx={{ fontSize: "0.95rem", color: "#687791", lineHeight: 1.6, mb: 3.5 }}
      >
        {description}
      </Typography>
    </>
  );

  const LoginButton = () => (
    <Box sx={{ position: "relative", mt: 3 }}>
      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={loading}
        endIcon={!loading && <ArrowForwardRounded />}
        sx={{
          py: 1.5,
          borderRadius: "14px",
          textTransform: "none",
          fontWeight: 800,
          fontSize: "0.94rem",
          background: loading
            ? "#e8edf6"
            : "linear-gradient(120deg, #193eaa, #5f51cc)",
          boxShadow: loading ? "none" : "0 12px 25px rgba(49,87,201,.28)",
          "&:hover": {
            background: "linear-gradient(120deg, #15358f, #4e40b9)",
          },
        }}
      >
        {loading ? "Logging in…" : "Log in"}
      </Button>
      {loading && (
        <CircularProgress
          size={22}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            mt: "-11px",
            ml: "-11px",
            color: "#fff",
          }}
        />
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 5fr) minmax(0, 7fr)" },
        background: "#f5f8ff",
      }}
    >
      <Paper
        square
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "linear-gradient(145deg, #f8faff 0%, #ffffff 58%)",
          borderRight: "1px solid #e9eef8",
          py: { xs: 4, sm: 5 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            mx: { xs: 2.5, sm: 5 },
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                p: "8px",
                borderRadius: "16px",
                background: "#fff",
                border: "1px solid #e7edfa",
                boxShadow: "0 10px 24px rgba(42,84,166,.12)",
              }}
            >
              <img
                src={logo}
                alt="PULSE"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  letterSpacing: "1.5px",
                  background: "linear-gradient(135deg, #173b9d, #7356d8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                PULSE
              </Typography>
              <Typography
                sx={{
                  fontSize: ".64rem",
                  color: "#75829a",
                  letterSpacing: ".7px",
                  textTransform: "uppercase",
                }}
              >
                Lab &amp; Clinic System
              </Typography>
            </Box>
          </Box>

          <Box
            role="tablist"
            aria-label="Login type"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: "4px",
              mb: 3.5,
              borderRadius: "14px",
              background: "#eef2fb",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 4,
                width: "calc(50% - 4px)",
                borderRadius: "10px",
                background: "#fff",
                boxShadow: "0 3px 10px rgba(25,62,170,.12)",
                transform:
                  mode === "doctor" ? "translateX(100%)" : "translateX(0)",
                transition: "transform 320ms cubic-bezier(.2,.8,.2,1)",
              }}
            />
            {[
              {
                value: "user",
                label: "User Login",
                icon: <PersonRounded fontSize="small" />,
              },
              {
                value: "doctor",
                label: "Doctor Login",
                icon: <LocalHospitalRounded fontSize="small" />,
              },
            ].map((tab) => (
              <Button
                key={tab.value}
                role="tab"
                aria-selected={mode === tab.value}
                onClick={() => switchMode(tab.value)}
                startIcon={tab.icon}
                sx={{
                  zIndex: 1,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.78rem",
                  color: mode === tab.value ? "#173b9d" : "#75829a",
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              width: "200%",
              display: "flex",
              transform:
                mode === "doctor" ? "translateX(-50%)" : "translateX(0)",
              transition: "transform 460ms cubic-bezier(.22,1,.36,1)",
            }}
          >
            <Box sx={{ width: "50%", pr: { xs: 0, sm: 0 } }}>
              <FormTitle
                icon={<ShieldRounded sx={{ fontSize: 15 }} />}
                title="USER WORKSPACE"
                description="Sign in to manage your laboratory with clarity and confidence."
              />
              <form onSubmit={submitUser} noValidate>
                <Typography
                  sx={{
                    fontSize: "0.77rem",
                    fontWeight: 800,
                    color: "#34425c",
                    mb: 1,
                    letterSpacing: "0.45px",
                    textTransform: "uppercase",
                  }}
                >
                  Your workspace ID
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1.1,
                  }}
                >
                  {[
                    { name: "stateCode", label: "State" },
                    { name: "orgCode", label: "Org" },
                    { name: "userCode", label: "User" },
                  ].map((field) => (
                    <TextField
                      key={field.name}
                      fullWidth
                      size="small"
                      inputMode="numeric"
                      label={field.label}
                      name={field.name}
                      value={userForm[field.name]}
                      onChange={updateForm(setUserForm, setUserErrors)}
                      error={Boolean(userErrors[field.name])}
                      helperText={userErrors[field.name]}
                      sx={fieldStyles}
                    />
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.77rem",
                    fontWeight: 800,
                    color: "#34425c",
                    mb: 1,
                    mt: 2.5,
                    letterSpacing: "0.45px",
                    textTransform: "uppercase",
                  }}
                >
                  Password
                </Typography>
                <PasswordField
                  value={userForm.password}
                  onChange={updateForm(setUserForm, setUserErrors)}
                  error={userErrors.password}
                  show={showUserPassword}
                  setShow={setShowUserPassword}
                />
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 1.25 }}
                >
                  <Button
                    onClick={() => navigate("/forgot-password")}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                    }}
                  >
                    Forgot password?
                  </Button>
                </Box>
                <LoginButton />
              </form>
            </Box>

            <Box sx={{ width: "50%" }}>
              <FormTitle
                icon={<LocalHospitalRounded sx={{ fontSize: 15 }} />}
                title="DOCTOR PORTAL"
                description="Sign in to access your clinical workspace securely."
              />
              <form onSubmit={submitDoctor} noValidate>
                <Typography
                  sx={{
                    fontSize: "0.77rem",
                    fontWeight: 800,
                    color: "#34425c",
                    mb: 1,
                    letterSpacing: "0.45px",
                    textTransform: "uppercase",
                  }}
                >
                  Doctor workspace ID
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1.1,
                  }}
                >
                  {[
                    { name: "username1", label: "State" },
                    { name: "username2", label: "Org" },
                    { name: "username3", label: "Doctor" },
                  ].map((field) => (
                    <TextField
                      key={field.name}
                      fullWidth
                      size="small"
                      inputMode="numeric"
                      label={field.label}
                      name={field.name}
                      value={doctorForm[field.name]}
                      onChange={updateForm(setDoctorForm, setDoctorErrors)}
                      error={Boolean(doctorErrors.username)}
                      helperText={
                        field.name === "username3" ? doctorErrors.username : ""
                      }
                      sx={fieldStyles}
                    />
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.77rem",
                    fontWeight: 800,
                    color: "#34425c",
                    mb: 1,
                    mt: 2.5,
                    letterSpacing: "0.45px",
                    textTransform: "uppercase",
                  }}
                >
                  Password
                </Typography>
                <PasswordField
                  value={doctorForm.password}
                  onChange={updateForm(setDoctorForm, setDoctorErrors)}
                  error={doctorErrors.password}
                  show={showDoctorPassword}
                  setShow={setShowDoctorPassword}
                />
                <LoginButton />
              </form>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 4,
              pt: 2.25,
              borderTop: "1px solid #e8edf6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: ".72rem", color: "#8190a8" }}>
              © {new Date().getFullYear()} PULSE
            </Typography>
            <Box sx={{ display: "flex", gap: 0.6, alignItems: "center" }}>
              <ShieldRounded sx={{ color: "#4b70d3", fontSize: 15 }} />
              <Typography
                sx={{ fontSize: ".72rem", color: "#667792", fontWeight: 700 }}
              >
                Protected sign-in
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Right Panel — Brand Visual ── */}
      <Grid
        item
        xs={false}
        lg={7}
        sm={6}
        sx={{
          position: "relative",
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(8, 23, 67, 0.88) 0%, rgba(25, 65, 160, 0.76) 52%, rgba(96, 69, 181, 0.63) 100%)",
            zIndex: 1,
          },
        }}
      >
        {/* Overlay content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            p: { sm: 4, lg: 6.5 },
          }}
        >
          <Box sx={{ mb: "auto", pt: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.9,
                px: 1.35,
                py: 0.7,
                borderRadius: "100px",
                background: "rgba(255,255,255,0.13)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
              }}
            >
              <AutoGraphRounded sx={{ color: "#bfcfff", fontSize: 18 }} />
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: "0.76rem",
                  letterSpacing: "0.5px",
                }}
              >
                CONNECTED CARE, SIMPLIFIED
              </Typography>
            </Box>
          </Box>

          {/* Quote card */}
          <Box
            sx={{
              background: "rgba(8, 20, 59, 0.22)",
              backdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "22px",
              p: { sm: 3, lg: 3.5 },
              maxWidth: 600,
              boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            }}
          >
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 750,
                color: "#ffffff",
                lineHeight: 1.45,
                mb: 1.5,
                letterSpacing: "-0.2px",
              }}
            >
              We don't just provide a platform; we provide a partnership that
              grows with your laboratory.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={logo}
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: "#ffffff",
                  }}
                >
                  PULSE
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}
                >
                  Intelligent lab operations
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Feature chips */}
          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            {[
              { label: "Patient EMR", icon: <BadgeRounded /> },
              { label: "Smart analytics", icon: <AutoGraphRounded /> },
              { label: "Always supported", icon: <SupportAgentRounded /> },
            ].map(({ label, icon }) => (
              <Box
                key={label}
                sx={{
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: "100px",
                  px: 1.4,
                  py: 0.65,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                }}
              >
                {React.cloneElement(icon, {
                  sx: { fontSize: 15, color: "#dce4ff" },
                })}
                <Typography
                  sx={{
                    fontSize: "0.73rem",
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 500,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};

export default NewLogin;
